













































import Vue from 'vue';
import Component from 'vue-class-component';
import {
	mapGetters, mapState, mapMutations, mapActions,
} from 'vuex';
import { sleep } from '@/utilities/vuexHelpers';
import AcceptedCreditCards from '@/components/AcceptedCreditCards.vue';
import SectionLoader from '@/components/SectionLoader.vue';
import CreditCard from '@/models/CreditCard';

const Setup = Vue.extend({
	name: 'SecureCard',
	inject: ['$validator'],
	components: { AcceptedCreditCards, SectionLoader },
	props: {
		captcha_token: String,
		required: Boolean,
		type: {
			type: String,
			default: 'payment',
		},
		validate: Boolean,
		refresh_happened: {
			type: String,
			default: null,
		},
		location_details: { type: Object },
	},
	computed: {
		...mapState(['paths']),
		...mapGetters('location', ['location']),
		...mapGetters(['customer']),
	},
	watch: {
		validate(value: boolean, oldValue: boolean) {
			if (value && value !== oldValue) {
				this.submit();
			}
		},
	},
	methods: {
		...mapMutations(['setLoading']),
		...mapActions('location', ['setLocation']),

		submit() {
			// override
		},
	},
});

@Component
export default class SecureCard extends Setup {
	public captcha_token!: string;
	public required!: boolean;
	public type!: string;
	public validate!: boolean;
	public location_details!: Locations.Location;

	public account: CreditCard = new CreditCard();
	public error_msg = '';
	public errorCount = 0;
	public formErrors: any = null;
	public iframeHeight: number | string = '0px';
	public loading: boolean = false;

	public get settings(): Locations.Settings {
		return this.$store.getters['location/settings'];
	}

	// Vuex
	public customerId!: number;
	public location!: Locations.Location;
	public paths!: { [pathName: string]: string };

	public get hasErrors(): boolean {
		return !!this.formErrors;
	}

	public get stylesheet(): string {
		return this.paths.css ? escape(encodeURIComponent(this.paths.css)) : '';
	}

	public get token(): string {
		return this.$store.state.location.token;
	}

	public get url(): string {
		const params = {
			customer_id: this.customer.customer,
			location: this.location.code,
			country: this.location.details.country,
			required: this.required,
			stylesheet: this.stylesheet,
			sub: this.location.subLocation,
			token: this.token,
			type: this.type,
		} as { [key: string]: string | number | boolean };

		let base = 'https://www.eparking.us/master_cc_form.php';
		if (this.settings.disable_hosted_cc_forms) {
			base = 'https://eparking.us/credit_card.php';
		}

		if (this.refresh_happened) {
			this.refresh_happened = `?refresh_happened=${this.refresh_happened || ''}`;
			base += this.refresh_happened;
			if (this.settings.disable_hosted_cc_forms) {
				base = 'https://eparking.us/credit_card.php';
			}
		}

		if ((this.location.server_environment as string) === 'development') {
			base = 'https://eparking.demo.netpark.us/master_cc_form.php';
			if (this.refresh_happened) {
				base += this.refresh_happened;
			}
			if (this.settings.disable_hosted_cc_forms) {
				base = 'https://eparking.demo.netpark.us/credit_card.php';
			}
		} else if ((this.location.server_environment as string) === 'local') {
			base = 'http://eparking.local/master_cc_form.php';
			if (this.refresh_happened) {
				base += this.refresh_happened;
			}
			if (this.settings.disable_hosted_cc_forms) {
				base = 'http://eparking.local/credit_card.php';
			}
		}

		return Object.keys(params).reduce((url: string, key: string) => {
			if (!params[key]) {
				return url;
			}

			let updatedUrl = url;

			updatedUrl += updatedUrl.indexOf('?') > 0 ? '&' : '?';
			updatedUrl += `${key}=${params[key]}`;

			return updatedUrl;
		}, base);
	}

	public async created() {
		this.fetchToken();

		window.addEventListener('message', (event) => {
			try {
				if (event.data.size) {
					this.resizeFrame(event.data.size);
				}

				if (event.data.errors) {
					this.handleErrors(event.data.errors);
					this.setLoading(false);
					this.error_msg = event.data.errors.creditCard;
				}

				if (event.data.message === 'No card') {
					this.$emit('on-tokenize', {});
					this.setLoading(false);
				}

				if (event.data.token) {
					this.handleValidToken(event.data);
					this.setLoading(false);
				}
			} catch (error) {
				/* Missing event data */
				this.setLoading(false);
			}
		}, false);

		if (this.location_details) {
			if (this.location.codeID !== this.location_details.codeID) {
				this.setLocation(this.location_details.codeID);
			}
		}
	}

	// not currently used - eventually should be used to prevent spamming the form
	public error() {
		if (this.errorCount > 10) {
			return;
		}

		this.errorCount += 1;
		const frame = this.$refs.iframe as HTMLIFrameElement;
		frame.src = '';
		frame.src = this.url;
	}

	public async fetchToken() {
		if (this.token) {
			return;
		}

		this.loading = true;
		let error = '';

		for (let i = 0; i < 5; i += 1) {
			// eslint-disable-next-line no-await-in-loop
			await sleep(i * 250);

			try {
				// eslint-disable-next-line no-await-in-loop
				await this.$store.dispatch('location/fetchToken', this.location.codeID);
				this.loading = false;
				return;
			} catch (e) {
				console.log(`Error fetching token ${e}`);
				error = e.toString();
			}
		}

		this.loading = false;
		this.error_msg = `Unable to load credit card form, please reload the page and try again. (${error})`;
	}

	public handleErrors(errors: any) {
		this.$set(this, 'formErrors', errors);
		this.$emit('on-tokenize', { errors });
	}

	public handleValidToken(card: CreditCard) {
		if (!this.account.token) {
			this.account = new CreditCard(card);
		}

		this.$nextTick(() => {
			this.$emit('on-tokenize', card);
		});
	}

	public mounted() {
		this.$emit('loaded');
	}

	public resetFocus(event: any) {
		if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].indexOf(event.target.nodeName) === -1) {
			(document.activeElement as any).blur();
		}

		if (this.$el.querySelector('iframe')) {
			((this.$el.querySelector('iframe') as any).contentWindow as any).postMessage({
				action: 'reset-focus',
			}, '*');
		}
	}

	public resizeFrame(size: number | string) {
		this.$nextTick(() => {
			this.iframeHeight = size;
		});
	}

	public sendMessage(data: any) {
		console.log('Tokenizing Card...');
		((this.$refs.iframe as HTMLIFrameElement).contentWindow as any).postMessage(data, '*');
	}

	public submit() {
		if (this.account.token) {
			this.handleValidToken(this.account);
		} else {
			this.error_msg = '';
			this.setLoading(true);
			this.sendMessage({
				action: 'tokenize',
				captcha_token: this.captcha_token,
			});
		}
	}
}
